<script lang="ts">
    import { onMount } from 'svelte';
    import { push } from 'svelte-spa-router';
    import { auth } from '../js/auth.js';
    import { api } from '../js/api.js';
    import { toast } from '../js/toast.js';

    let step: 'profile' | 'plan' = 'profile';

    // Profile fields
    let firstName = '';
    let lastName = '';
    let mobile = '';
    let teamName = '';
    let errors: Record<string, string> = {};

    // Plan selection
    let plans: any[] = [];
    let selectedPlanUuid: string | null = null;
    let isLoadingPlans = false;
    let isSubmitting = false;

    // Team invitation flow - skip team name and plan selection
    let pendingTeamInvite: string | null = null;

    onMount(async () => {
        // If user already completed onboarding, redirect to dashboard
        if ($auth.user?.onboarding_completed) {
            push('/dashboard');
            return;
        }

        // Check if user is joining via team invitation
        pendingTeamInvite = localStorage.getItem('pending_team_invite');

        if (!pendingTeamInvite) {
            await loadPlans();
        }
    });

    async function loadPlans() {
        isLoadingPlans = true;
        try {
            const response = await api.getPaymentPlans() as any;
            if (response.success) {
                plans = response.data.plans || [];
            }
        } catch (error) {
            console.error('Error loading plans:', error);
        } finally {
            isLoadingPlans = false;
        }
    }

    async function handleProfileSubmit() {
        if (!validateProfileForm()) return;

        if (pendingTeamInvite) {
            // Team invite flow: skip plan, complete onboarding and accept invite
            await handleTeamInviteComplete();
        } else {
            step = 'plan';
        }
    }

    function validateProfileForm() {
        let valid = true;
        errors = {};

        if (!firstName.trim()) {
            errors['firstName'] = 'First name is required';
            valid = false;
        }
        if (!lastName.trim()) {
            errors['lastName'] = 'Last name is required';
            valid = false;
        }
        if (!pendingTeamInvite && !teamName.trim()) {
            errors['teamName'] = 'Team name is required';
            valid = false;
        }
        return valid;
    }

    async function handleTeamInviteComplete() {
        isSubmitting = true;
        try {
            // Complete onboarding without team/plan (joining existing team)
            const result = await auth.completeOnboarding({
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                mobile: mobile.trim() || null,
                team_invite_token: pendingTeamInvite,
            });

            if (result.success && pendingTeamInvite) {
                const acceptRes = await api.acceptTeamInvitation(pendingTeamInvite);
                localStorage.removeItem('pending_team_invite');

                if (acceptRes.success && acceptRes.data?.team_uuid) {
                    auth.update(state => {
                        if (state.user) state.user.active_team_uuid = acceptRes.data.team_uuid;
                        return state;
                    });
                    toast.showNotification(true, `Welcome! You've joined ${acceptRes.data?.team_name || 'the team'}.`, 'top');
                } else {
                    toast.showNotification(true, 'Account set up successfully.', 'top');
                }

                await api.getResourceLists();
                push('/dashboard');
            } else {
                toast.showNotification(false, result.message || 'Something went wrong. Please try again.', 'top');
            }
        } catch (error) {
            toast.showNotification(false, 'Something went wrong. Please try again.', 'top');
        } finally {
            isSubmitting = false;
        }
    }

    async function handleComplete() {
        if (!selectedPlanUuid) {
            toast.showNotification(false, 'Please select a plan to try', 'top');
            return;
        }

        isSubmitting = true;
        try {
            const result = await auth.completeOnboarding({
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                mobile: mobile.trim() || null,
                team_name: teamName.trim(),
                payment_plan_uuid: selectedPlanUuid,
            });

            if (result.success) {
                toast.showNotification(true, 'Welcome to Xplorit! Your 14-day free trial has started.', 'top');
                await api.getResourceLists();
                push('/dashboard');
            } else {
                toast.showNotification(false, result.message || 'Something went wrong. Please try again.', 'top');
            }
        } catch (error) {
            toast.showNotification(false, 'Something went wrong. Please try again.', 'top');
        } finally {
            isSubmitting = false;
        }
    }

    function formatPrice(price: number): string {
        return new Intl.NumberFormat(navigator.language, {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(price);
    }

    function getPlanHighlights(plan: any): string[] {
        const highlights: string[] = [];
        if (plan.included_briefs === 0) {
            highlights.push('Pay per brief');
        } else if (plan.included_briefs === -1) {
            highlights.push('Unlimited briefs');
        } else {
            highlights.push(`${plan.included_briefs} briefs/mo`);
        }
        if (plan.max_team_seats === null) {
            highlights.push('Unlimited seats');
        } else if (plan.max_team_seats === 1) {
            highlights.push('1 user');
        } else {
            highlights.push(`${plan.max_team_seats} team seats`);
        }
        if (plan.features && Array.isArray(plan.features)) {
            // Pick first 1-2 short features
            const shortFeatures = plan.features
                .filter((f: string) => f.length < 40)
                .slice(0, 1);
            highlights.push(...shortFeatures);
        }
        return highlights;
    }

    // Clear errors on input
    $: if (errors['firstName'] && firstName) errors['firstName'] = '';
    $: if (errors['lastName'] && lastName) errors['lastName'] = '';
    $: if (errors['teamName'] && teamName) errors['teamName'] = '';
</script>

<style>
    .onboarding-page {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #e8f0fe 0%, #f0e6ff 50%, #e0f4ff 100%);
        padding: 2rem 1rem;
    }

    .onboarding-card {
        background: var(--white);
        border-radius: 16px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
        width: 100%;
        max-width: 480px;
        padding: 2.5rem 2rem;
    }

    .onboarding-card.wide {
        max-width: 520px;
    }

    .onboarding-logo {
        text-align: center;
        margin-bottom: 1.5rem;
    }

    .onboarding-logo img {
        height: 48px;
    }

    .onboarding-header {
        text-align: center;
        margin-bottom: 1.5rem;
    }

    .onboarding-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--grey);
        margin: 0 0 0.25rem 0;
    }

    .onboarding-subtitle {
        font-size: 0.875rem;
        color: var(--grey-secondary);
        margin: 0;
        line-height: 1.5;
    }

    .onboarding-form {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .form-row {
        display: flex;
        gap: 1rem;
    }

    .form-row > .auth-form-field {
        flex: 1;
    }

    .step-indicator {
        display: flex;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
    }

    .step-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--border-color, #e5e7eb);
        transition: all 0.3s;
    }

    .step-dot.active {
        background: var(--primary);
        width: 24px;
        border-radius: 4px;
    }

    .step-dot.completed {
        background: var(--primary);
    }

    /* Plan cards */
    .plan-cards {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
    }

    .plan-card {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem 1.25rem;
        border: 2px solid var(--border-color, #e5e7eb);
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s;
        background: var(--white);
    }

    .plan-card:hover {
        border-color: var(--primary);
        background: rgba(5, 108, 242, 0.02);
    }

    .plan-card.selected {
        border-color: var(--primary);
        background: rgba(5, 108, 242, 0.05);
        box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
    }

    .plan-radio {
        width: 20px;
        height: 20px;
        border: 2px solid var(--border-color, #d1d5db);
        border-radius: 50%;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
    }

    .plan-card.selected .plan-radio {
        border-color: var(--primary);
    }

    .plan-radio-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--primary);
        transform: scale(0);
        transition: transform 0.2s;
    }

    .plan-card.selected .plan-radio-dot {
        transform: scale(1);
    }

    .plan-card-info {
        flex: 1;
        min-width: 0;
    }

    .plan-card-name {
        font-weight: 600;
        font-size: 1rem;
        color: var(--grey);
        margin: 0;
    }

    .plan-card-desc {
        font-size: 0.8rem;
        color: var(--grey-secondary);
        margin: 0.15rem 0 0 0;
        line-height: 1.4;
    }

    .plan-highlights {
        display: flex;
        flex-wrap: wrap;
        gap: 0.35rem;
        margin-top: 0.5rem;
    }

    .plan-highlight {
        display: inline-flex;
        align-items: center;
        font-size: 0.7rem;
        color: var(--primary);
        background: rgba(5, 108, 242, 0.08);
        padding: 0.2rem 0.5rem;
        border-radius: 4px;
        font-weight: 500;
        white-space: nowrap;
    }

    .plan-card-price {
        font-weight: 700;
        font-size: 1.1rem;
        color: var(--grey);
        white-space: nowrap;
        flex-shrink: 0;
    }

    .plan-card-price-suffix {
        font-size: 0.75rem;
        font-weight: 400;
        color: var(--grey-secondary);
    }

    .trial-note {
        text-align: center;
        font-size: 0.8rem;
        color: var(--grey-secondary);
        margin-top: 0.75rem;
        line-height: 1.5;
    }

    .trial-note strong {
        color: #16a34a;
    }

    .back-link {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        background: none;
        border: none;
        color: var(--primary);
        font-size: 0.85rem;
        font-weight: 500;
        cursor: pointer;
        padding: 0;
        margin-bottom: 1rem;
    }

    .back-link:hover {
        color: var(--darkprimary);
    }

    .back-link i {
        margin: 0;
    }

    .submit-button {
        width: 100%;
        padding: 0.85rem 1.5rem;
        border: none;
        border-radius: 8px;
        background: var(--primary);
        color: var(--white);
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s, transform 0.1s;
        font-family: "Poppins", sans-serif;
    }

    .submit-button:hover:not(:disabled) {
        background: var(--darkprimary);
    }

    .submit-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .trial-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        background: #ecfdf5;
        color: #16a34a;
        font-size: 0.8rem;
        font-weight: 600;
        padding: 0.35rem 0.75rem;
        border-radius: 20px;
        margin-bottom: 1rem;
    }

    @media (max-width: 480px) {
        .form-row {
            flex-direction: column;
            gap: 1rem;
        }

        .onboarding-card {
            padding: 2rem 1.5rem;
        }
    }
</style>

<div class="onboarding-page">
    <div class="onboarding-card" class:wide={step === 'plan'}>
        <div class="onboarding-logo">
            <img src="./src/assets/xplorit_color.png" alt="Xplorit Logo">
        </div>

        {#if !pendingTeamInvite}
            <div class="step-indicator">
                <div class="step-dot" class:active={step === 'profile'} class:completed={step === 'plan'}></div>
                <div class="step-dot" class:active={step === 'plan'}></div>
            </div>
        {/if}

        {#if step === 'plan'}
            <!-- Step 2: Plan Selection -->
            <button class="back-link" on:click={() => step = 'profile'}>
                <i class="arrow left icon"></i>
                Back
            </button>

            <div class="onboarding-header">
                <div class="trial-badge">14 days free</div>
                <h1 class="onboarding-title">Choose your plan</h1>
                <p class="onboarding-subtitle">Try any plan free for 14 days. No credit card required.</p>
            </div>

            {#if isLoadingPlans}
                <div style="text-align: center; padding: 2rem; color: var(--grey-secondary);">
                    <i class="spinner loading icon"></i> Loading plans...
                </div>
            {:else}
                <div class="plan-cards">
                    {#each plans as plan (plan.uuid)}
                        <div
                            class="plan-card"
                            class:selected={selectedPlanUuid === plan.uuid}
                            on:click={() => selectedPlanUuid = plan.uuid}
                            on:keydown={(e) => e.key === 'Enter' && (selectedPlanUuid = plan.uuid)}
                            role="radio"
                            aria-checked={selectedPlanUuid === plan.uuid}
                            tabindex="0"
                        >
                            <div class="plan-radio">
                                <div class="plan-radio-dot"></div>
                            </div>
                            <div class="plan-card-info">
                                <p class="plan-card-name">{plan.name}</p>
                                {#if plan.description}
                                    <p class="plan-card-desc">{plan.description}</p>
                                {/if}
                                {#if selectedPlanUuid === plan.uuid}
                                    <div class="plan-highlights">
                                        {#each getPlanHighlights(plan) as highlight}
                                            <span class="plan-highlight">{highlight}</span>
                                        {/each}
                                    </div>
                                {/if}
                            </div>
                            <div class="plan-card-price">
                                {formatPrice(plan.price)}<span class="plan-card-price-suffix">{plan.included_briefs === 0 ? '/brief' : '/mo'}</span>
                            </div>
                        </div>
                    {/each}
                </div>

                <button
                    type="button"
                    class="submit-button"
                    on:click={handleComplete}
                    disabled={!selectedPlanUuid || isSubmitting}
                >
                    {isSubmitting ? 'Setting up your account...' : 'Start Free Trial'}
                </button>

                <p class="trial-note">
                    <strong>No credit card required.</strong> You'll get full access for 14 days.
                </p>
            {/if}

        {:else}
            <!-- Step 1: Profile & Team -->
            <div class="onboarding-header">
                <h1 class="onboarding-title">Tell us about yourself</h1>
                <p class="onboarding-subtitle">
                    {#if pendingTeamInvite}
                        Just a few details to finish setting up your account.
                    {:else}
                        We just need a few details to set up your account.
                    {/if}
                </p>
            </div>

            <form class="onboarding-form" on:submit|preventDefault={handleProfileSubmit}>
                <div class="form-row">
                    <div class="auth-form-field" class:error={errors['firstName']}>
                        <label for="first-name" class="auth-form-label">First Name</label>
                        <input
                            id="first-name"
                            type="text"
                            class="auth-form-input"
                            bind:value={firstName}
                            placeholder="First name"
                            aria-invalid={errors['firstName'] ? 'true' : 'false'}
                            autocomplete="given-name"
                        >
                        {#if errors['firstName']}
                            <span class="auth-error-message" role="alert">{errors['firstName']}</span>
                        {/if}
                    </div>

                    <div class="auth-form-field" class:error={errors['lastName']}>
                        <label for="last-name" class="auth-form-label">Last Name</label>
                        <input
                            id="last-name"
                            type="text"
                            class="auth-form-input"
                            bind:value={lastName}
                            placeholder="Last name"
                            aria-invalid={errors['lastName'] ? 'true' : 'false'}
                            autocomplete="family-name"
                        >
                        {#if errors['lastName']}
                            <span class="auth-error-message" role="alert">{errors['lastName']}</span>
                        {/if}
                    </div>
                </div>

                <div class="auth-form-field">
                    <label for="mobile" class="auth-form-label">Mobile <span style="color: var(--grey-secondary); font-weight: 400;">(optional)</span></label>
                    <input
                        id="mobile"
                        type="tel"
                        class="auth-form-input"
                        bind:value={mobile}
                        placeholder="Your mobile number"
                        autocomplete="tel"
                    >
                </div>

                {#if !pendingTeamInvite}
                    <div class="auth-form-field" class:error={errors['teamName']}>
                        <label for="team-name" class="auth-form-label">Team Name</label>
                        <input
                            id="team-name"
                            type="text"
                            class="auth-form-input"
                            bind:value={teamName}
                            placeholder="Your company or team name"
                            aria-invalid={errors['teamName'] ? 'true' : 'false'}
                        >
                        {#if errors['teamName']}
                            <span class="auth-error-message" role="alert">{errors['teamName']}</span>
                        {/if}
                    </div>
                {/if}

                <button type="submit" class="submit-button" disabled={isSubmitting}>
                    {#if pendingTeamInvite}
                        {isSubmitting ? 'Setting up your account...' : 'Complete Setup'}
                    {:else}
                        Continue
                    {/if}
                </button>
            </form>
        {/if}
    </div>
</div>
